// ── Time formatting ───────────────────────────────────────────

/**
 * Converts "14:30" - "2:30 PM"
 */
export const formatTime = (time) => {
  if (!time) return '';
  const [hours, mins] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(mins).padStart(2, '0')} ${period}`;
};

/**
 * Converts a seconds value - "m:ss"
 */
export const formatCountdown = (seconds) => {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Converts a seconds duration - "1H 23M" or "45M"
 */
export const formatDuration = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}H ${m}M`;
  return `${m}M`;
};


// ── Date helpers ──────────────────────────────────────────────

/**
 * Returns the number of days between today and a date string ("2025-06-01").
 * Negative = in the past.
 */
export const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

/* Converts a daysUntil number into a human label */
export const getDateLabel = (daysUntil) => {
  if (daysUntil === null) return null;
  if (daysUntil < 0) return `${Math.abs(daysUntil)} days ago`;
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  return `In ${daysUntil} days`;
};

/* Returns "Morning", "Afternoon", or "Evening" based on current hour */
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Morning';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
};


// ── Timetable / category styles ───────────────────────────────

/* Returns a Tailwind bg+border class string for a timetable category */
export const getCategoryStyle = (category) => {
  const styles = {
    class:    'bg-blue-100 border-blue-600',
    work:     'bg-purple-100 border-purple-600',
    exercise: 'bg-red-100 border-red-600',
    personal: 'bg-green-100 border-green-600',
    other:    'bg-yellow-100 border-yellow-600',
  };
  return styles[category] || styles.other;
};

/* Returns a solid Tailwind bg class for a timetable category */
export const getCategoryColor = (category) => {
  const colors = {
    class:    'bg-blue-600',
    work:     'bg-purple-600',
    exercise: 'bg-red-600',
    personal: 'bg-green-600',
    other:    'bg-yellow-600',
  };
  return colors[category] || colors.other;
};


// ── Schedule urgency ──────────────────────────────────────────

/* Returns a Tailwind border+bg class string based on how urgent an item is. Used in: Schedule */
export const getUrgencyColor = (daysUntil, type, status) => {
  if (daysUntil === null) return 'border-neutral-900 bg-white';
  if (status === 'completed') return 'border-green-600 bg-green-100';
  if (daysUntil < 0) return 'border-red-600 bg-red-100';

  if (type === 'assignment' || type === 'exam') {
    if (daysUntil === 0)  return 'border-red-600 bg-red-100';
    if (daysUntil <= 3)   return 'border-red-400 bg-red-50';
    if (daysUntil <= 7)   return 'border-orange-400 bg-orange-50';
    if (daysUntil <= 14)  return 'border-yellow-400 bg-yellow-50';
  } else {
    if (daysUntil === 0)  return 'border-blue-600 bg-blue-100';
    if (daysUntil <= 3)   return 'border-blue-400 bg-blue-50';
    if (daysUntil <= 7)   return 'border-green-400 bg-green-50';
  }
  return 'border-neutral-900 bg-white';
};

/* Returns a Tailwind text color for assignment priority. */
export const getPriorityColor = (priority) => {
  const colors = { high: 'text-red-600', medium: 'text-yellow-600', low: 'text-green-600' };
  return colors[priority] || colors.medium;
};